import { FC } from 'react';
import { useI18nContext } from '@i18n/i18n-react';
import useTheme from '@base/useTheme';
import { ChartType, Layout, ChartTileVisualization } from '@common/api';
import { Button } from '@components/button/Button';
import { DEFAULT_CHART, GROUPED_CHARTS } from '@components/chart';
import { FormField, NumberInputField } from '@components/form';
import { GridColumns, Margins } from '@components/icons/FarseerIcons';
import { FlexGrow, FlexRow } from '@components/layout/Flex';
import { Popover } from '@components/popover/Popover';
import { ToggleGroup } from '@components/toggle/ToggleGroup';
import { TooltipWrapper } from '@components/tooltip/Tooltip';
import { Update } from '@use-gpu/state';

import { ChartGridEdit } from './ChartGridEdit';

export type ChartLayoutPickerProps = {
    chart: ChartTileVisualization;
    onChange: (update: Update<ChartTileVisualization | undefined>) => void;
    canEdit: boolean;
};

export const ChartLayoutPicker: FC<ChartLayoutPickerProps> = (props: ChartLayoutPickerProps) => {
    const { chart, onChange, canEdit } = props;

    const { LL } = useI18nContext();
    const { colors } = useTheme();

    const tileChartType = chart.type;

    const isPie = tileChartType === ChartType.PieChart;
    const canGroup = tileChartType && GROUPED_CHARTS.includes(tileChartType);

    const tileChartLayout: Layout | null = !isPie ? chart.layout ?? Layout.Horizontal : null;

    const tileChartGrid: boolean | null = canGroup ? !!chart.series.groupByDepth : null;

    const gridColumns: number = chart.display.gridColumns ?? 0;

    return tileChartLayout != null || tileChartGrid != null ? (
        <>
            <FormField label={LL.ORIENTATION_LAYOUT()}>
                <FlexRow $gap={8}>
                    {tileChartLayout ? (
                        <ToggleGroup
                            $size="small"
                            disabled={!canEdit}
                            value={tileChartLayout}
                            onChange={(layout) =>
                                onChange({
                                    layout,
                                })
                            }
                        >
                            <ToggleGroup.Toggle title={LL.HORIZONTAL()} value={Layout.Horizontal} />
                            <ToggleGroup.Toggle title={LL.VERTICAL()} value={Layout.Vertical} />
                        </ToggleGroup>
                    ) : null}

                    {canGroup ? (
                        <ToggleGroup
                            $size="small"
                            disabled={!canEdit}
                            value={tileChartGrid}
                            onChange={(groupByDepth: boolean) => onChange({ series: { groupByDepth } })}
                        >
                            <TooltipWrapper label={LL.GRID_SINGLE_CHART_EXPLANATION()}>
                                <ToggleGroup.Toggle title={LL.SINGLE()} value={false} />
                            </TooltipWrapper>
                            <TooltipWrapper label={LL.GRID_MULTIPLE_CHART_EXPLANATION()}>
                                <ToggleGroup.Toggle title={LL.GRID()} value={true} />
                            </TooltipWrapper>
                        </ToggleGroup>
                    ) : null}

                    <FlexGrow />

                    <Popover placement="bottom-end">
                        <Popover.Trigger>
                            <Button icon={<Margins />} $type="quaternary" />
                        </Popover.Trigger>
                        <Popover.Content width={345} $padding="7px" noFocus>
                            <div style={{ padding: 1 }}>
                                <ChartGridEdit chart={chart} onChange={onChange} canEdit={canEdit} />
                            </div>
                        </Popover.Content>
                    </Popover>
                </FlexRow>
            </FormField>

            {tileChartGrid ? (
                <NumberInputField
                    inline
                    label={LL.GRID_COLUMNS()}
                    value={gridColumns}
                    type="int"
                    empty={DEFAULT_CHART.display.gridColumns}
                    min={1}
                    step={1}
                    width={65}
                    upDown
                    prefix={<GridColumns color={colors.neutral.w700} />}
                    onChange={(gridColumns) => onChange({ display: { gridColumns } })}
                />
            ) : null}
        </>
    ) : null;
};
